import { Box, Card, Flex, Skeleton } from '@radix-ui/themes';
import { PitchPerformanceHelper } from 'classes/helpers/pitch-performance.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { AxisExtentLabel } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/axis-label';
import { BreakReferenceArea } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/reference-area';
import { PitchPerfPlotHwdLegend } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/hwd-legend';
import { PlateCross } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plate-location/cross';
import { PlateDeltaLabel } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plate-location/delta-label';
import { PlateDot } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plate-location/dot';
import {
  PLATE_PLOT_MAX_IN,
  getAveragePlateError,
  getMeanCenteredPlateLocations,
  getShotPlateError,
  isWithinPlatePlot,
  plateErrorToPlotCoords,
  plateToPlotCoords,
} from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plate-location/plot-data';
import { PlateTarget } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plate-location/target';
import { PlateTooltip } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plate-location/tooltip';
import { PlotHeader } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-header';
import { usePlotHover } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-hover';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  AXIS_COLOR_ON_DARK,
  AXIS_COLOR_ON_LIGHT,
  GRID_COLOR_ON_DARK,
  GRID_COLOR_ON_LIGHT,
} from 'enums/canvas';
import {
  IPitchPerfPlot,
  IPlatePoint,
  IPlotCoords,
  IPlotCoordsLine,
  IPlotThresholdCircle,
  IScatterShapePlatePoint,
  PREVIOUS_MS_FILL,
  PREVIOUS_MS_STROKE,
} from 'interfaces/i-pitch-performance';
import { IAxisExtentLabel } from 'interfaces/recharts/i-axis-labels';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { MAX_PLATE_DELTA_IN } from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IGroupedFires } from 'lib_ts/interfaces/data/i-pitch-performance';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { range, sum } from 'lodash';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useMiscStore from 'stores/use-misc-store';

enum PlotMode {
  Actual = 'Actual',
  MeanCentered = 'Mean Centered',
  TargetCentered = 'Target Centered',
  TargetCenteredAvg = 'Target Centered (Avg)',
}

const DRAW_DELTA_LABELS = false;
const DRAW_CONNECTORS = false;

// if plotting at most this many shots, draw each shot's target + line + label automatically
const ACTUAL_TARGETS_SHOTS_THRESHOLD = 1;

const PLOT_TICK_INTERVAL_IN = 10;
const PLOT_DOMAIN: [number, number] = [-PLATE_PLOT_MAX_IN, PLATE_PLOT_MAX_IN];
const PLOT_TICKS = range(
  -PLATE_PLOT_MAX_IN,
  PLATE_PLOT_MAX_IN + PLOT_TICK_INTERVAL_IN,
  PLOT_TICK_INTERVAL_IN
);
const CHART_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };
const REF_GROUP_OPACITY = 0.75;
const CENTER_TARGET_RGB = '150,150,150';
const PLOT_CENTER: IPlotCoords = { x: 0, y: 0 };

const STRIKE_ZONE = {
  left: VISIBLE_STRIKEZONE.left_ft * FT_TO_INCHES,
  right: VISIBLE_STRIKEZONE.right_ft * FT_TO_INCHES,
  bottom:
    (VISIBLE_STRIKEZONE.bottom_ft - VISIBLE_STRIKEZONE.plate_ft.plate_z) *
    FT_TO_INCHES,
  top:
    (VISIBLE_STRIKEZONE.top_ft - VISIBLE_STRIKEZONE.plate_ft.plate_z) *
    FT_TO_INCHES,
};

const PLATE_AXIS_EXTENT_LABELS: IAxisExtentLabel[] = [
  {
    key: 'x-max',
    x: PLATE_PLOT_MAX_IN,
    y: 0,
    text: `${PLATE_PLOT_MAX_IN}"`,
    dx: -4,
    dy: 16,
    textAnchor: 'end',
  },
  {
    key: 'x-min',
    x: -PLATE_PLOT_MAX_IN,
    y: 0,
    text: `-${PLATE_PLOT_MAX_IN}"`,
    dx: 4,
    dy: 16,
    textAnchor: 'start',
  },
  {
    key: 'y-max',
    x: 0,
    y: PLATE_PLOT_MAX_IN,
    text: `${PLATE_PLOT_MAX_IN}"`,
    dx: -4,
    dy: 16,
    textAnchor: 'end',
  },
  {
    key: 'y-min',
    x: 0,
    y: -PLATE_PLOT_MAX_IN,
    text: `-${PLATE_PLOT_MAX_IN}"`,
    dx: -4,
    dy: -8,
    textAnchor: 'end',
  },
];

export const PitchPerfPlotPlate = (props: IPitchPerfPlot) => {
  const loading = usePitchPerformanceStore((s) => s.loading);
  const selectedShotIds = usePitchPerformanceStore((s) => s.selectedShotIds);
  const showThresholds = usePitchPerformanceStore((s) => s.showThresholds);

  const [mode, setMode] = useState<PlotMode>(PlotMode.MeanCentered);
  const { pointOpacity, hoverHandlers } = usePlotHover();

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const axisColor = isDarkMode ? AXIS_COLOR_ON_DARK : AXIS_COLOR_ON_LIGHT;
  const gridColor = isDarkMode ? GRID_COLOR_ON_DARK : GRID_COLOR_ON_LIGHT;

  const plotData = useMemo(() => {
    const actualPoints: IPlatePoint[] = [];
    const refPoints: IPlatePoint[] = [];
    const targetPoints: IPlatePoint[] = [];
    const connectors: IPlotCoordsLine[] = [];
    const labels: IPlatePoint[] = [];

    const getColors = (group: IGroupedFires) => ({
      fillRgb:
        !props.highlightCurrent || group.isCurrentMs
          ? group.rgb
          : PREVIOUS_MS_FILL,
      strokeRgb:
        !props.highlightCurrent || group.isCurrentMs
          ? group.rgb
          : PREVIOUS_MS_STROKE,
    });

    const selectedShots = (shots: IMachineShot[]) =>
      shots.filter(
        ({ _id }) =>
          selectedShotIds.length === 0 || selectedShotIds.includes(_id)
      );

    const addPoint = (points: IPlatePoint[], point: IPlatePoint) => {
      if (isWithinPlatePlot(point)) {
        points.push(point);
      }
    };

    const centeredTarget: IPlatePoint = {
      fillRgb: CENTER_TARGET_RGB,
      strokeRgb: CENTER_TARGET_RGB,
      pitchName: 'Plate Center (Target)',
      msHash: undefined,
      shotId: undefined,
      isTarget: true,
      x: 0,
      y: 0,
    };

    switch (mode) {
      case PlotMode.Actual: {
        const totalShots =
          sum(
            props.groups.map(
              (group) =>
                selectedShots(group.fires.map(({ shot }) => shot)).filter(
                  ({ traj }) => traj
                ).length
            )
          ) ?? 0;
        const showTargets = totalShots <= ACTUAL_TARGETS_SHOTS_THRESHOLD;

        for (const group of props.groups) {
          const colors = getColors(group);
          const shots = selectedShots(group.fires.map(({ shot }) => shot));

          for (const shot of shots) {
            if (!shot.traj || !shot.target_traj) {
              continue;
            }

            const actual = plateToPlotCoords(TrajHelper.getPlateLoc(shot.traj));
            const target = plateToPlotCoords(
              TrajHelper.getPlateLoc(shot.target_traj)
            );
            const point: IPlatePoint = {
              ...colors,
              ...PitchPerformanceHelper.hoverKeys(group),
              pitchName: group.pitch.name,
              msHash: group.target.ms.matching_hash,
              shotId: shot._id,
              isTarget: false,
              x: actual.x,
              y: actual.y,
              target: showTargets ? target : undefined,
              delta: showTargets
                ? {
                    x: actual.x - target.x,
                    y: actual.y - target.y,
                  }
                : undefined,
            };

            addPoint(actualPoints, point);

            if (!showTargets) {
              continue;
            }

            addPoint(targetPoints, {
              ...colors,
              ...PitchPerformanceHelper.hoverKeys(group),
              pitchName: `${group.pitch.name} (Target)`,
              msHash: group.target.ms.matching_hash,
              shotId: undefined,
              isTarget: true,
              x: target.x,
              y: target.y,
            });

            connectors.push({
              key: shot._id,
              ...PitchPerformanceHelper.hoverKeys(group),
              from: actual,
              to: target,
              rgb: colors.strokeRgb,
            });

            if (isWithinPlatePlot(point)) {
              labels.push(point);
            }
          }
        }

        for (const group of props.refGroups ?? []) {
          for (const shot of selectedShots(
            group.fires.map(({ shot }) => shot)
          )) {
            if (!shot.traj) {
              continue;
            }

            const actual = plateToPlotCoords(TrajHelper.getPlateLoc(shot.traj));
            addPoint(refPoints, {
              fillRgb: group.rgb,
              strokeRgb: group.rgb,
              ...PitchPerformanceHelper.refHoverKeys(group),
              pitchName: `${group.pitch.name} (Reference)`,
              msHash: group.target.ms.matching_hash,
              shotId: shot._id,
              isTarget: false,
              x: actual.x,
              y: actual.y,
            });
          }
        }
        break;
      }

      case PlotMode.TargetCentered: {
        for (const group of props.groups) {
          const colors = getColors(group);

          for (const shot of selectedShots(
            group.fires.map(({ shot }) => shot)
          )) {
            const error = getShotPlateError(shot);

            if (!error) {
              continue;
            }

            const actual = plateErrorToPlotCoords(error);
            addPoint(actualPoints, {
              ...colors,
              ...PitchPerformanceHelper.hoverKeys(group),
              pitchName: group.pitch.name,
              msHash: group.target.ms.matching_hash,
              shotId: shot._id,
              isTarget: false,
              x: actual.x,
              y: actual.y,
              target: PLOT_CENTER,
              delta: actual,
              onlyShowDeltas: true,
            });
          }
        }

        for (const group of props.refGroups ?? []) {
          for (const shot of selectedShots(
            group.fires.map(({ shot }) => shot)
          )) {
            const error = getShotPlateError(shot);

            if (!error) {
              continue;
            }

            const actual = plateErrorToPlotCoords(error);
            addPoint(refPoints, {
              fillRgb: group.rgb,
              strokeRgb: group.rgb,
              ...PitchPerformanceHelper.refHoverKeys(group),
              pitchName: `${group.pitch.name} (Reference)`,
              msHash: group.target.ms.matching_hash,
              shotId: shot._id,
              isTarget: false,
              x: actual.x,
              y: actual.y,
              target: PLOT_CENTER,
              delta: actual,
              onlyShowDeltas: true,
            });
          }
        }

        targetPoints.push(centeredTarget);
        break;
      }

      case PlotMode.TargetCenteredAvg: {
        for (const group of props.groups) {
          const colors = getColors(group);
          const average = getAveragePlateError(
            selectedShots(group.fires.map(({ shot }) => shot))
          );

          if (!average) {
            continue;
          }

          const actual = plateErrorToPlotCoords(average);
          const point: IPlatePoint = {
            ...colors,
            ...PitchPerformanceHelper.hoverKeys(group),
            pitchName: `${group.pitch.name} (Average)`,
            msHash: group.target.ms.matching_hash,
            shotId: undefined,
            isTarget: false,
            x: actual.x,
            y: actual.y,
            target: PLOT_CENTER,
            delta: actual,
            onlyShowDeltas: true,
          };

          addPoint(actualPoints, point);
          if (isWithinPlatePlot(point)) {
            labels.push(point);
          }
        }

        for (const group of props.refGroups ?? []) {
          const average = getAveragePlateError(
            selectedShots(group.fires.map(({ shot }) => shot))
          );

          if (!average) {
            continue;
          }

          const actual = plateErrorToPlotCoords(average);
          addPoint(refPoints, {
            fillRgb: group.rgb,
            strokeRgb: group.rgb,
            ...PitchPerformanceHelper.refHoverKeys(group),
            pitchName: `${group.pitch.name} (Reference Average)`,
            msHash: group.target.ms.matching_hash,
            shotId: undefined,
            isTarget: false,
            x: actual.x,
            y: actual.y,
            target: PLOT_CENTER,
            delta: actual,
            onlyShowDeltas: true,
          });
        }

        targetPoints.push(centeredTarget);
        break;
      }

      case PlotMode.MeanCentered: {
        for (const group of props.groups) {
          const colors = getColors(group);
          const plates = getMeanCenteredPlateLocations(
            selectedShots(group.rotatedShots)
          );

          for (const plate of plates) {
            const actual = plateToPlotCoords(plate);
            addPoint(actualPoints, {
              ...colors,
              ...PitchPerformanceHelper.hoverKeys(group),
              pitchName: group.pitch.name,
              msHash: group.target.ms.matching_hash,
              shotId: plate.shotId,
              isTarget: false,
              x: actual.x,
              y: actual.y,
              target: PLOT_CENTER,
              delta: actual,
              onlyShowDeltas: true,
            });
          }
        }

        for (const group of props.refGroups ?? []) {
          const plates = getMeanCenteredPlateLocations(
            selectedShots(group.rotatedShots)
          );

          for (const plate of plates) {
            const actual = plateToPlotCoords(plate);
            addPoint(refPoints, {
              fillRgb: group.rgb,
              strokeRgb: group.rgb,
              ...PitchPerformanceHelper.refHoverKeys(group),
              pitchName: `${group.pitch.name} (Reference)`,
              msHash: group.target.ms.matching_hash,
              shotId: plate.shotId,
              isTarget: false,
              x: actual.x,
              y: actual.y,
              target: PLOT_CENTER,
              delta: actual,
              onlyShowDeltas: true,
            });
          }
        }

        targetPoints.push(centeredTarget);
        break;
      }
    }

    return {
      actualPoints,
      connectors,
      labels,
      refPoints,
      targetPoints,
    };
  }, [
    mode,
    props.groups,
    props.highlightCurrent,
    props.refGroups,
    selectedShotIds,
  ]);

  const thresholdCircles = useMemo(() => {
    if (!showThresholds || mode === PlotMode.Actual) {
      return [];
    }

    const circle: IPlotThresholdCircle = {
      key: 'plate-center',
      radius: MAX_PLATE_DELTA_IN,
      rgb: isDarkMode ? '255,255,255' : '0,0,0',
      x: 0,
      y: 0,
    };

    return [circle];
  }, [isDarkMode, mode, showThresholds]);

  return (
    <ErrorBoundary identifier="PitchPerfPlotPlate">
      <Skeleton loading={loading}>
        <Flex direction="column" gap="3">
          <PlotHeader
            title="Plate Location"
            subtitle="Plot of plate locations from catcher's view"
          >
            <CommonSelectInput
              id="plate-plot-mode"
              variant="soft"
              inputColor={RADIX.COLOR.NEUTRAL}
              size="1"
              value={mode}
              onChange={(value) => setMode(value as PlotMode)}
              options={[
                {
                  label: 'Mean-centered',
                  value: PlotMode.MeanCentered,
                  description: 'Visualize the spray of the pitch',
                },
                {
                  label: 'Target-centered',
                  value: PlotMode.TargetCentered,
                  description: 'Visualize the plate error',
                },
                {
                  label: 'Target-centered (Averaged)',
                  value: PlotMode.TargetCenteredAvg,
                  description: 'Visualize the average plate error',
                },
                {
                  label: 'Actual',
                  value: PlotMode.Actual,
                  description: 'Visualize the true plate location',
                },
              ]}
              skipSort
            />
          </PlotHeader>

          <Card className="no-padding">
            <ResponsiveContainer width="100%" aspect={1}>
              <ScatterChart margin={CHART_MARGIN}>
                <CartesianGrid stroke={gridColor} />

                <XAxis
                  type="number"
                  dataKey="x"
                  name="Plate X"
                  domain={PLOT_DOMAIN}
                  ticks={PLOT_TICKS}
                  hide
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name="Plate Z"
                  domain={PLOT_DOMAIN}
                  ticks={PLOT_TICKS}
                  hide
                />

                <ReferenceLine x={0} stroke={axisColor} />
                <ReferenceLine y={0} stroke={axisColor} />

                {PLATE_AXIS_EXTENT_LABELS.map((label) => (
                  <ReferenceDot
                    key={`axis-label-${label.key}`}
                    x={label.x}
                    y={label.y}
                    r={0}
                    fill={axisColor}
                    stroke="none"
                    ifOverflow="visible"
                    shape={<AxisExtentLabel {...label} />}
                  />
                ))}

                <ReferenceArea
                  x1={STRIKE_ZONE.left}
                  x2={STRIKE_ZONE.right}
                  y1={STRIKE_ZONE.bottom}
                  y2={STRIKE_ZONE.top}
                  fill="none"
                  stroke={axisColor}
                  strokeWidth={2}
                />

                {thresholdCircles.map((circle) => (
                  <ReferenceArea
                    key={`threshold-${circle.key}`}
                    x1={circle.x - circle.radius}
                    x2={circle.x + circle.radius}
                    y1={circle.y - circle.radius}
                    y2={circle.y + circle.radius}
                    ifOverflow="hidden"
                    fill={`rgb(${circle.rgb})`}
                    fillOpacity={0.2}
                    stroke={`rgb(${circle.rgb})`}
                    strokeOpacity={0.75}
                    strokeWidth={1}
                    shape={<BreakReferenceArea />}
                  />
                ))}

                {DRAW_CONNECTORS &&
                  plotData.connectors.map((connector) => (
                    <ReferenceLine
                      key={`connector-${connector.key}`}
                      segment={[connector.from, connector.to]}
                      ifOverflow="hidden"
                      stroke={`rgb(${connector.rgb})`}
                      strokeOpacity={pointOpacity(connector, 0.75)}
                    />
                  ))}

                <Scatter
                  name="Reference"
                  data={plotData.refPoints}
                  shape={(point: IScatterShapePlatePoint) => (
                    <PlateCross
                      cx={point.cx}
                      cy={point.cy}
                      payload={point.payload}
                      opacity={pointOpacity(point.payload, REF_GROUP_OPACITY)}
                      {...hoverHandlers(point.payload?.itemKey)}
                    />
                  )}
                  isAnimationActive={false}
                />
                <Scatter
                  name="Delivered"
                  data={plotData.actualPoints}
                  shape={(point: IScatterShapePlatePoint) => (
                    <PlateDot
                      cx={point.cx}
                      cy={point.cy}
                      payload={point.payload}
                      opacity={pointOpacity(point.payload)}
                      {...hoverHandlers(point.payload?.itemKey)}
                    />
                  )}
                  isAnimationActive={false}
                />
                <Scatter
                  name="Target"
                  data={plotData.targetPoints}
                  shape={(point: IScatterShapePlatePoint) => (
                    <PlateTarget
                      cx={point.cx}
                      cy={point.cy}
                      payload={point.payload}
                      opacity={pointOpacity(point.payload)}
                      {...hoverHandlers(point.payload?.itemKey)}
                    />
                  )}
                  isAnimationActive={false}
                />

                {DRAW_DELTA_LABELS &&
                  plotData.labels.map((point, index) => (
                    <ReferenceDot
                      key={`label-${index}-${point.x}-${point.y}`}
                      x={point.x}
                      y={point.y}
                      r={0}
                      ifOverflow="hidden"
                      shape={
                        <PlateDeltaLabel
                          point={point}
                          opacity={pointOpacity(point)}
                        />
                      }
                    />
                  ))}

                <Tooltip
                  content={<PlateTooltip />}
                  animationDuration={0}
                  cursor={false}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Card>

          {props.isHwDiagnostic && (
            <Box
              display={{
                md: 'inline',
                lg: 'none',
              }}
            >
              <PitchPerfPlotHwdLegend />
            </Box>
          )}
        </Flex>
      </Skeleton>
    </ErrorBoundary>
  );
};
